import Button from "@/common/components/Button";
import Spinner from "@/common/components/Spinner";
import { cn } from "@/common/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { startOfMonth } from "date-fns";
import { PencilIcon, PenOffIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Layout,
  LayoutItem,
  Responsive as ResponsiveGridLayout,
  useContainerWidth,
} from "react-grid-layout";
import { DASHBOARD_GRID_CONFIG, DASHBOARD_LAYOUT_ITEM_CONFIG, DASHBOARD_LAYOUT_ITEM_CONFIG_BY_KIND } from "../../constants/dashboards/dashboardGridConfig.constant";
import { useUpdateDashboardComponentsPosition } from "../../hooks/dashboards/useUpdateDashboardComponentsPosition";
import { DashboardData } from "../../types/dashboards/dashboard.type";
import { DashboardComponentData, DashboardComponentFiltersProp } from "../../types/dashboards/dashboardComponent.type";
import { getInitialPosition } from "../../utils/dashboards/getInitialPosition";
import CreateDashboardComponent from "./CreateDashboardComponent";
import DashboardBadgeFilters from "./DashboardBadgeFilters";
import DashboardComponent from "./DashboardComponent";
import DashboardHeader from "./DashboardHeader";
import DashboardSheetFilters from "./DashboardSheetFilters";
import DeleteDashboardComponent from "./DeleteDashboardComponent";
import UpdateDashboardComponent from "./UpdateDashboardComponent";

interface DashboardGridProps {
  dashboard: DashboardData;
  dashboardUpdatedAt: number;
  initialWidth: number;
  isCreator: boolean;
  hasPermissionToUpdateDashboard: boolean;
}

const DashboardGrid = ({
  dashboard,
  dashboardUpdatedAt,
  initialWidth,
  isCreator,
  hasPermissionToUpdateDashboard,
}: DashboardGridProps) => {
  const queryClient = useQueryClient();

  const scrollViewRef = useRef<HTMLDivElement>(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const [dashboardComponentIdToDelete, setDashboardComponentIdToDelete] = useState<string>();
  const [dashboardComponentToUpdate, setDashboardComponentToUpdate] = useState<DashboardComponentData>();

  const [shouldScrollToNewComponent, setShouldScrollToNewComponent] = useState(false);

  const localComponentsRef = useRef(dashboard.components);
  const [localComponentsVersion, setLocalComponentsVersion] = useState(0);

  const isEditable = isCreator && hasPermissionToUpdateDashboard && isEditMode;

  useEffect(() => {
    localComponentsRef.current = dashboard.components;
    setLocalComponentsVersion(v => v + 1);
  }, [dashboardUpdatedAt]);

  const defaultDate = useMemo(() => {
    const today = new Date();

    return {
      from: startOfMonth(today),
      to: today,
    };
  }, []);

  const [filters, setFilters] = useState<DashboardComponentFiltersProp>({
    date: defaultDate,
  });

  const {
    mutate: updatePosition,
    isPending: updatePositionIsPending,
  } = useUpdateDashboardComponentsPosition({
    dashboardId: dashboard.id,
    options: {
      onMutate: () => {
        queryClient.cancelQueries({
          queryKey: ['updateDashboardComponentsPosition', dashboard.id],
        });
      },
    },
  });

  const { width, containerRef, mounted } = useContainerWidth({
    initialWidth,
  });

  const layout: Layout | undefined = useMemo(() => {
    return dashboard.components?.map((dashboardComponent) => ({
      ...DASHBOARD_LAYOUT_ITEM_CONFIG,
      ...(DASHBOARD_LAYOUT_ITEM_CONFIG_BY_KIND[dashboardComponent.generator_properties?.kind] ?? {}),
      i: dashboardComponent.id,
      x: dashboardComponent.start_x,
      y: dashboardComponent.start_y,
      w: dashboardComponent.width,
      h: dashboardComponent.height,
    }) as LayoutItem);
  }, [dashboardUpdatedAt]);

  const initialPosition = useMemo(() => {
    return getInitialPosition(localComponentsRef.current);
  }, [localComponentsVersion]);

  const handleDelete = useCallback((id: string) => {
    setDashboardComponentIdToDelete(id);
  }, []);

  const handleChange = useCallback((obj: unknown | Layout) => {
    const newLayout = obj as Layout;

    localComponentsRef.current = localComponentsRef.current?.map(comp => {
      const item = newLayout.find(l => l.i === comp.id);

      if (!item) return comp;

      return { ...comp, start_x: item.x, start_y: item.y, width: item.w, height: item.h };
    });

    setLocalComponentsVersion(v => v + 1);
    updatePosition(newLayout);
  }, []);

  const onCreateComponent = useCallback(() => {
    setShouldScrollToNewComponent(true);
  }, []);

  useEffect(() => {
    if (!shouldScrollToNewComponent) {
      return;
    }

    setTimeout(() => {
      scrollViewRef.current?.scroll({
        top: initialPosition.y * DASHBOARD_GRID_CONFIG.rowHeight!,
        behavior: "smooth",
      });
    }, 0);

    setShouldScrollToNewComponent(false);
  }, [dashboardUpdatedAt, initialPosition.y]);

  return (
    <div className="flex-1 flex flex-col h-full gap-4 relative">
      <div className="flex items-center justify-between gap-3 h-10">
        <div className="flex items-center gap-3">
          <DashboardHeader
            dashboard={dashboard}
            dashboardUpdatedAt={dashboardUpdatedAt}
            isCreator={isCreator}
            hasPermissionToUpdateDashboard={hasPermissionToUpdateDashboard}
          />

          <div className="inline-flex items-center gap-2">
            {width > 950 && (
              <DashboardBadgeFilters
                filters={filters}
                setFilters={setFilters}
                dashboardDesiredFilters={dashboard.desired_filters}
                width={width}
              />
            )}

            <DashboardSheetFilters
              filters={filters}
              setFilters={setFilters}
              dashboardDesiredFilters={dashboard.desired_filters}
              width={width}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isCreator && (
            <>
              {isEditable && (
                <CreateDashboardComponent
                  dashboardId={dashboard.id}
                  initialPosition={initialPosition}
                  onCreateComponent={onCreateComponent}
                  width={width}
                  filters={filters}
                  dashboardDesiredFilters={dashboard.desired_filters}
                />
              )}

              <Button
                variant="success"
                onClick={() => setIsEditMode(prev => !prev)}
                operationKlassToCheckPermission="Client::Dashboards::Update"
                className={cn(
                  'transition-colors text-nowrap h-10',
                  isEditMode && 'bg-yellow-op-2',
                  (!width || width >= 580) && 'w-40 px-0'
                )}
              >
                {width && width < 580
                  ? (!isEditMode ? <PencilIcon size={16} /> : <PenOffIcon size={16} />)
                  : (!isEditMode ? 'Habilitar edição' : 'Desabilitar edição')}
              </Button>
            </>
          )}

          {!isCreator && (
            <>
              <p className="italic text-white-400 text-sm">
                Dashboard compartilhado
              </p>
            </>
          )}
        </div>
      </div>

      {!!updatePositionIsPending && (
        <div
          className="flex items-center gap-2 absolute bg-black-300 p-3 z-50 mx-auto rounded-lg shadow-xl self-center top-10"
          style={{ backdropFilter: "blur(24px)" }}
        >
          <Spinner className="fill-white-400 size-4" />

          <span className="text-sm italic text-white-400">
            Salvando...
          </span>
        </div>
      )}

      <div
        ref={scrollViewRef}
        className="flex-1 overflow-auto thin-scroll h-full pb-24"
      >
        <div ref={containerRef as React.RefObject<HTMLDivElement>}>
          {mounted && layout && (
            <ResponsiveGridLayout
              layouts={{
                lg: layout,
                sm: layout,
              }}
              width={width}
              onResizeStop={handleChange}
              onDragStop={handleChange}
              {...DASHBOARD_GRID_CONFIG}
              dragConfig={{ enabled: isEditable }}
              dropConfig={{ enabled: isEditable }}
              resizeConfig={{ enabled: isEditable }}
            >
              {dashboard?.components?.map((dashboardComponent) => (
                <DashboardComponent
                  key={dashboardComponent.id}
                  dashboardDesiredFilters={dashboard.desired_filters}
                  dashboardComponent={dashboardComponent}
                  onDelete={handleDelete}
                  onUpdate={setDashboardComponentToUpdate}
                  filters={filters}
                  isEditable={isEditable}
                  hasPermissionToUpdateDashboard={hasPermissionToUpdateDashboard}
                />
              ))}
            </ResponsiveGridLayout>
          )}
        </div>
      </div>

      <DeleteDashboardComponent
        isOpen={!!dashboardComponentIdToDelete}
        setIsOpen={(value) => {
          if (!value) {
            setDashboardComponentIdToDelete(undefined);
          }
        }}
        dashboardId={dashboard.id}
        dashboardComponentId={dashboardComponentIdToDelete}
      />

      <UpdateDashboardComponent
        isOpen={!!dashboardComponentToUpdate}
        setIsOpen={(value) => {
          if (!value) {
            setDashboardComponentToUpdate(undefined);
          }
        }}
        dashboardId={dashboard.id}
        dashboardComponent={dashboardComponentToUpdate}
      />
    </div>
  );
};

export default DashboardGrid;
